import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { getPortalContext } from '@/lib/portal';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import WorkflowsPage from '@/pages/WorkflowsPage';
import WorkflowBuilderPage from '@/pages/WorkflowBuilderPage';
import CampaignsPage from '@/pages/CampaignsPage';
import SegmentsPage from '@/pages/SegmentsPage';
import TemplatesPage from '@/pages/TemplatesPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import BlogAutomationPage from '@/pages/BlogAutomationPage';
import SettingsPage from '@/pages/SettingsPage';
import MembershipManagementPage from '@/pages/MembershipManagementPage';
import CompanyInfoPage from '@/pages/CompanyInfoPage';
import KnowledgeBasePage from '@/pages/KnowledgeBasePage';
import KnowledgeBaseArticlePage from '@/pages/KnowledgeBaseArticlePage';
import WebVitalsPage from '@/pages/WebVitalsPage';
import CompetitorHeatmapPage from '@/pages/CompetitorHeatmapPage';
import MarketIntelligencePage from '@/pages/MarketIntelligencePage';
import AnalyticsInsightsPage from '@/pages/AnalyticsInsightsPage';
import KeywordDemandPage from '@/pages/KeywordDemandPage';
import GBPDashboardPage from '@/pages/GBPDashboardPage';
import SERPTrackingPage from '@/pages/SERPTrackingPage';
import ShareOfSearchPage from '@/pages/ShareOfSearchPage';
import ContentGapPage from '@/pages/ContentGapPage';
import SEOIntelligencePage from '@/pages/SEOIntelligencePage';
import MarketingAutomationEnginePage from '@/pages/MarketingAutomationEnginePage';
import CampaignGeneratorPage from '@/pages/CampaignGeneratorPage';
import QRCampaignsPage from '@/pages/QRCampaignsPage';
import QRCampaignDetailPage from '@/pages/QRCampaignDetailPage';
import LeadScoringPage from '@/pages/LeadScoringPage';
import CustomersPage from '@/pages/CustomersPage';
import DNCPage from '@/pages/DNCPage';
import ABTestingHubPage from '@/pages/ABTestingHubPage';
import AIAgentMonitorPage from '@/pages/AIAgentMonitorPage';
import AIAgentWorkflowPage from '@/pages/AIAgentWorkflowPage';
import WordPressSEOPage from '@/pages/WordPressSEOPage';
import ServiceAreaHubsPage from '@/pages/ServiceAreaHubsPage';
import SystemDocumentationPage from '@/pages/SystemDocumentationPage';
import GBPManagerPage from '@/pages/GBPManagerPage';
import PredictiveMaintenancePage from '@/pages/PredictiveMaintenancePage';
import NurtureSequencesPage from '@/pages/NurtureSequencesPage';
import DataDeletionPage from '@/pages/DataDeletionPage';
import UnsubscribePage from '@/pages/UnsubscribePage';
import FacebookManagerPage from '@/pages/FacebookManagerPage';
import GoogleAdsCampaignManagerPage from '@/pages/GoogleAdsCampaignManagerPage';
import AutomatedBidManagerPage from '@/pages/AutomatedBidManagerPage';
import QualityScoreMonitorPage from '@/pages/QualityScoreMonitorPage';
import SearchQueryAnalyzerPage from '@/pages/SearchQueryAnalyzerPage';
import NegativeKeywordManagerPage from '@/pages/NegativeKeywordManagerPage';
import CampaignHealthMonitorPage from '@/pages/CampaignHealthMonitorPage';
import BudgetOptimizerPage from '@/pages/BudgetOptimizerPage';
import ConversionTrackingPage from '@/pages/ConversionTrackingPage';
import GoogleAdsReportingSuitePage from '@/pages/GoogleAdsReportingSuitePage';
import AdCopyPerformancePage from '@/pages/AdCopyPerformancePage';
import AuctionInsightsDashboardPage from '@/pages/AuctionInsightsDashboardPage';
import ShoppingCampaignManagerPage from '@/pages/ShoppingCampaignManagerPage';
import DisplayCampaignManagerPage from '@/pages/DisplayCampaignManagerPage';
import CallPerformanceDashboardPage from '@/pages/CallPerformanceDashboardPage';
import ConversionAttributionPage from '@/pages/ConversionAttributionPage';
import LSAManagerPage from '@/pages/LSAManagerPage';
import ReviewsMonitorPage from '@/pages/ReviewsMonitorPage';
import PromoCodesPage from '@/pages/PromoCodesPage';
import Layout from '@/components/Layout';
import { ErrorBoundary, PageErrorBoundary } from '@/components/ErrorBoundary';
import TechLayout from '@/components/TechLayout';
import TechAdminPage from '@/pages/TechAdminPage';
import TechAdminContestsPage from '@/pages/TechAdminContestsPage';
import TechAccountPage from '@/pages/TechAccountPage';
import TechDashboardPage from '@/pages/TechDashboardPage';
import TechDiagnosticSessionPage from '@/pages/TechDiagnosticSessionPage';
import TechDiagnosticsPage from '@/pages/TechDiagnosticsPage';
import TechEnrollPage from '@/pages/TechEnrollPage';
import TechLoginPage from '@/pages/TechLoginPage';
import TechPerformancePage from '@/pages/TechPerformancePage';
import TechPastCallsPage from '@/pages/TechPastCallsPage';
import TechPortfolioPage from '@/pages/TechPortfolioPage';
import TechPresentationPage from '@/pages/TechPresentationPage';
import TechLeaderboardPage from '@/pages/TechLeaderboardPage';
import TechSpiffsPage from '@/pages/TechSpiffsPage';
import TechResetPasswordPage from '@/pages/TechResetPasswordPage';
import TechSchedulePage from '@/pages/TechSchedulePage';
import TechJobPage from '@/pages/TechJobPage';
import TechDiagnosticPresentationPage from '@/pages/TechDiagnosticPresentationPage';
import TechDiagnosticInvoicePage from '@/pages/TechDiagnosticInvoicePage';
import CommercialProposalPage from '@/pages/CommercialProposalPage';
import MembershipLandingPage from '@/pages/MembershipLandingPage';
import MembershipPortalPage from '@/pages/MembershipPortalPage';

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );
}

function NotAvailableHost() {
  const { hostname } = window.location;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-gray-900">Site not available</h1>
        <p className="mt-2 text-gray-600">
          This domain (<span className="font-mono">{hostname}</span>) is not served by this application.
        </p>
      </div>
    </div>
  );
}

function MarketingPrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <FullPageSpinner />;
  return user ? <>{children}</> : <Navigate to="/login" />;
}

function TechPrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, technician, loading, error } = useTechAuth();

  if (loading) return <FullPageSpinner />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900">Access denied</h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            type="button"
            className="mt-4 px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800"
            onClick={() => window.location.assign('/login')}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  if (!user || !technician) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function TechManagerRoute({ children }: { children: React.ReactNode }) {
  const { technician } = useTechAuth();
  const role = String(technician?.role || '').trim().toLowerCase();

  if (role !== 'manager') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function MarketingRootRoute() {
  const { user, loading } = useAuth();

  if (loading) return <FullPageSpinner />;
  return <Navigate to={user ? '/admin' : '/login'} replace />;
}

function TechRootRoute() {
  const { user, loading } = useTechAuth();

  if (loading) return <FullPageSpinner />;
  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
}

function MarketingRoutes() {
  return (
    <Routes>
      <Route path="/" element={<MarketingRootRoute />} />
      {/* Legacy non-/admin paths that may be bookmarked or linked from older UI */}
      <Route path="/campaigns" element={<Navigate to="/admin/campaigns" replace />} />
      <Route path="/workflows" element={<Navigate to="/admin/workflows" replace />} />
      <Route path="/segments" element={<Navigate to="/admin/segments" replace />} />
      <Route path="/templates" element={<Navigate to="/admin/templates" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route
        path="/admin"
        element={
          <MarketingPrivateRoute>
            <Layout />
          </MarketingPrivateRoute>
        }
      >
        <Route index element={<PageErrorBoundary><DashboardPage /></PageErrorBoundary>} />
        <Route path="workflows" element={<PageErrorBoundary><WorkflowsPage /></PageErrorBoundary>} />
        <Route path="workflows/:id" element={<PageErrorBoundary><WorkflowBuilderPage /></PageErrorBoundary>} />
        <Route path="workflows/new" element={<PageErrorBoundary><WorkflowBuilderPage /></PageErrorBoundary>} />
        <Route path="campaigns" element={<PageErrorBoundary><CampaignsPage /></PageErrorBoundary>} />
        <Route path="qr-tracking" element={<PageErrorBoundary><QRCampaignsPage /></PageErrorBoundary>} />
        <Route path="qr-tracking/:id" element={<PageErrorBoundary><QRCampaignDetailPage /></PageErrorBoundary>} />
        <Route path="segments" element={<PageErrorBoundary><SegmentsPage /></PageErrorBoundary>} />
        <Route path="templates" element={<PageErrorBoundary><TemplatesPage /></PageErrorBoundary>} />
        <Route path="analytics" element={<PageErrorBoundary><AnalyticsPage /></PageErrorBoundary>} />
        <Route path="blog-automation" element={<PageErrorBoundary><BlogAutomationPage /></PageErrorBoundary>} />
        <Route path="facebook-manager" element={<PageErrorBoundary><FacebookManagerPage /></PageErrorBoundary>} />
        <Route path="membership-management" element={<PageErrorBoundary><MembershipManagementPage /></PageErrorBoundary>} />
        <Route path="memberships" element={<PageErrorBoundary><MembershipManagementPage /></PageErrorBoundary>} />
        <Route path="promo-codes" element={<PageErrorBoundary><PromoCodesPage /></PageErrorBoundary>} />
        <Route path="company-info" element={<PageErrorBoundary><CompanyInfoPage /></PageErrorBoundary>} />
        <Route path="knowledge-base" element={<PageErrorBoundary><KnowledgeBasePage /></PageErrorBoundary>} />
        <Route path="knowledge-base/:slug" element={<PageErrorBoundary><KnowledgeBaseArticlePage /></PageErrorBoundary>} />
        <Route path="web-vitals" element={<PageErrorBoundary><WebVitalsPage /></PageErrorBoundary>} />
        <Route path="competitor-heatmap" element={<PageErrorBoundary><CompetitorHeatmapPage /></PageErrorBoundary>} />
        <Route path="market-intelligence" element={<PageErrorBoundary><MarketIntelligencePage /></PageErrorBoundary>} />
        <Route path="analytics-insights" element={<PageErrorBoundary><AnalyticsInsightsPage /></PageErrorBoundary>} />
        <Route path="keyword-demand" element={<PageErrorBoundary><KeywordDemandPage /></PageErrorBoundary>} />
        <Route path="gbp-dashboard" element={<PageErrorBoundary><GBPDashboardPage /></PageErrorBoundary>} />
        <Route path="serp-tracking" element={<PageErrorBoundary><SERPTrackingPage /></PageErrorBoundary>} />
        <Route path="share-of-search" element={<PageErrorBoundary><ShareOfSearchPage /></PageErrorBoundary>} />
        <Route path="content-gap" element={<PageErrorBoundary><ContentGapPage /></PageErrorBoundary>} />
        <Route path="seo-intelligence" element={<PageErrorBoundary><SEOIntelligencePage /></PageErrorBoundary>} />
        <Route path="automation-engine" element={<PageErrorBoundary><MarketingAutomationEnginePage /></PageErrorBoundary>} />
        <Route path="campaign-generator" element={<PageErrorBoundary><CampaignGeneratorPage /></PageErrorBoundary>} />
        <Route path="lead-scoring" element={<PageErrorBoundary><LeadScoringPage /></PageErrorBoundary>} />
        <Route path="customers" element={<PageErrorBoundary><CustomersPage /></PageErrorBoundary>} />
        <Route path="dnc" element={<PageErrorBoundary><DNCPage /></PageErrorBoundary>} />
        <Route path="ab-testing" element={<PageErrorBoundary><ABTestingHubPage /></PageErrorBoundary>} />
        <Route path="ai-agent" element={<PageErrorBoundary><AIAgentMonitorPage /></PageErrorBoundary>} />
        <Route path="ai-agent-workflow" element={<PageErrorBoundary><AIAgentWorkflowPage /></PageErrorBoundary>} />
        <Route path="wordpress-seo" element={<PageErrorBoundary><WordPressSEOPage /></PageErrorBoundary>} />
        <Route path="service-area-hubs" element={<PageErrorBoundary><ServiceAreaHubsPage /></PageErrorBoundary>} />
        <Route path="documentation" element={<PageErrorBoundary><SystemDocumentationPage /></PageErrorBoundary>} />
        <Route path="gbp-manager" element={<PageErrorBoundary><GBPManagerPage /></PageErrorBoundary>} />
        <Route path="predictive-maintenance" element={<PageErrorBoundary><PredictiveMaintenancePage /></PageErrorBoundary>} />
        <Route path="nurture-sequences" element={<PageErrorBoundary><NurtureSequencesPage /></PageErrorBoundary>} />
        <Route path="call-performance" element={<PageErrorBoundary><CallPerformanceDashboardPage /></PageErrorBoundary>} />
        <Route path="google-ads" element={<PageErrorBoundary><GoogleAdsCampaignManagerPage /></PageErrorBoundary>} />
        <Route path="google-ads/bids" element={<PageErrorBoundary><AutomatedBidManagerPage /></PageErrorBoundary>} />
        <Route path="google-ads/quality-score" element={<PageErrorBoundary><QualityScoreMonitorPage /></PageErrorBoundary>} />
        <Route path="google-ads/search-queries" element={<PageErrorBoundary><SearchQueryAnalyzerPage /></PageErrorBoundary>} />
        <Route path="google-ads/negative-keywords" element={<PageErrorBoundary><NegativeKeywordManagerPage /></PageErrorBoundary>} />
        <Route path="google-ads/health" element={<PageErrorBoundary><CampaignHealthMonitorPage /></PageErrorBoundary>} />
        <Route path="google-ads/budget" element={<PageErrorBoundary><BudgetOptimizerPage /></PageErrorBoundary>} />
        <Route path="google-ads/conversions" element={<PageErrorBoundary><ConversionTrackingPage /></PageErrorBoundary>} />
        <Route path="google-ads/reporting" element={<PageErrorBoundary><GoogleAdsReportingSuitePage /></PageErrorBoundary>} />
        <Route path="google-ads/ad-copy-performance" element={<PageErrorBoundary><AdCopyPerformancePage /></PageErrorBoundary>} />
        <Route path="google-ads/auction-insights" element={<PageErrorBoundary><AuctionInsightsDashboardPage /></PageErrorBoundary>} />
        <Route path="google-ads/shopping" element={<PageErrorBoundary><ShoppingCampaignManagerPage /></PageErrorBoundary>} />
        <Route path="google-ads/display" element={<PageErrorBoundary><DisplayCampaignManagerPage /></PageErrorBoundary>} />
        <Route path="google-ads/attribution" element={<PageErrorBoundary><ConversionAttributionPage /></PageErrorBoundary>} />
        <Route path="lsa-manager" element={<PageErrorBoundary><LSAManagerPage /></PageErrorBoundary>} />
        <Route path="reviews-monitor" element={<PageErrorBoundary><ReviewsMonitorPage /></PageErrorBoundary>} />
        <Route path="settings" element={<PageErrorBoundary><SettingsPage /></PageErrorBoundary>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function TechRoutes() {
  return (
    <Routes>
      <Route path="/" element={<TechRootRoute />} />
      <Route path="/login" element={<TechLoginPage />} />
      <Route path="/reset-password" element={<TechResetPasswordPage />} />
      <Route
        path="/presentation"
        element={
          <TechPrivateRoute>
            <TechPresentationPage />
          </TechPrivateRoute>
        }
      />
      <Route
        path="/diagnostics/:id/presentation"
        element={
          <TechPrivateRoute>
            <TechDiagnosticPresentationPage />
          </TechPrivateRoute>
        }
      />
      <Route
        path="/diagnostics/:id/invoice"
        element={
          <TechPrivateRoute>
            <TechDiagnosticInvoicePage />
          </TechPrivateRoute>
        }
      />
      <Route
        element={
          <TechPrivateRoute>
            <TechLayout />
          </TechPrivateRoute>
        }
      >
        <Route path="/dashboard" element={<TechDashboardPage />} />
        <Route path="/schedule" element={<TechSchedulePage />} />
        <Route path="/job/:jobId" element={<TechJobPage />} />
        <Route path="/leaderboard" element={<TechLeaderboardPage />} />
        <Route path="/account" element={<TechAccountPage />} />
        <Route path="/enroll" element={<TechEnrollPage />} />
        <Route path="/diagnostics" element={<TechDiagnosticsPage />} />
        <Route path="/diagnostics/:id" element={<TechDiagnosticSessionPage />} />
        <Route path="/past-calls" element={<TechPastCallsPage />} />
        <Route path="/spiffs" element={<TechSpiffsPage />} />
        <Route path="/portfolio" element={<TechPortfolioPage />} />
        <Route
          path="/admin"
          element={
            <TechManagerRoute>
              <TechAdminPage />
            </TechManagerRoute>
          }
        />
        <Route
          path="/admin/performance"
          element={
            <TechManagerRoute>
              <TechPerformancePage />
            </TechManagerRoute>
          }
        />
        <Route
          path="/admin/contests"
          element={
            <TechManagerRoute>
              <TechAdminContestsPage />
            </TechManagerRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MembershipsRoutes() {
  return (
    <Routes>
      {/* Customer-facing membership landing page */}
      <Route path="/" element={<PageErrorBoundary><MembershipLandingPage /></PageErrorBoundary>} />

      {/* Member portal for existing members */}
      <Route path="/portal" element={<PageErrorBoundary><MembershipPortalPage /></PageErrorBoundary>} />

      {/* Commercial proposal page - public access */}
      <Route path="/proposal/:slug" element={<PageErrorBoundary><CommercialProposalPage /></PageErrorBoundary>} />

      {/* Fallback to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  const portal = getPortalContext();

  if (portal.portal === 'tech') {
    return <TechRoutes />;
  }

  if (portal.portal === 'memberships') {
    return <MembershipsRoutes />;
  }

  if (portal.portal === 'marketing') {
    return <MarketingRoutes />;
  }

  return <NotAvailableHost />;
}

function App() {
  return (
    <ErrorBoundary>
      <AppRoutes />
    </ErrorBoundary>
  );
}

export default App;
